import { matchPath } from "react-router";
import { ONTOLOGY_QUERY_KEYS, readOntologySelectionFromQuery, withOntologyView } from "../../features/ontology/ontologySelection";
import { parseContentAddress } from "../../shared/references/contentReferences";
import { appPaths, ROUTE_PATTERNS } from "../../shared/routing/appPaths";
import { mainPathOf, sidePathOf, SPLIT_PARAMETER, type AddressParts } from "./workspaceAddress";
import type { AppPath } from "./workspaceTypes";

/**
 * Old links that named two pages, translated once into a pair of routes (WA-10, SB-03, F90):
 * the workspace opens the main route as a tab and the second one beside it. The pinned
 * version, the segment and the entry anchor stay in the main route.
 *
 * - `<page>?split=<path>` (the split view before tabs) → the page, and the path beside it;
 * - `/documents/<ref>?beside=thread&thread=<id>` → the document, and the Thread beside it;
 * - `…?beside=ontology` with the ontology's keys → the page, and the ontology route beside it;
 * - `/threads/<id>?beside=source` → the Thread, and its subject document beside it, which
 *   only the server knows (`subjectOfThread`).
 *
 * This file is the only code that reads `beside` and `split`.
 */
const BESIDE_PARAMETER = "beside";
const BESIDE_THREAD_PARAMETER = "thread";

export type LegacyLinkTranslation =
  | { readonly kind: "pair"; readonly main: AppPath; readonly side: AppPath | null }
  | { readonly kind: "subjectOfThread"; readonly main: AppPath; readonly threadId: string };

function pathWith(address: AddressParts, query: URLSearchParams): AppPath {
  const search = query.toString();
  return `${address.pathname}${search === "" ? "" : `?${search}`}${address.hash}`;
}

function ontologyRoute(query: URLSearchParams): AppPath {
  const selection = readOntologySelectionFromQuery(query);
  const path = selection.version === null ? appPaths.ontology() : appPaths.ontology({ version: selection.version, termIri: selection.termIri });
  const view = withOntologyView(new URLSearchParams(), selection).toString();
  return view === "" ? path : `${path}?${view}`;
}

/** The pair an old link names, or `null` for an address that names one page. */
export function translateLegacyLink(address: AddressParts): LegacyLinkTranslation | null {
  const query = new URLSearchParams(address.search);
  const beside = query.get(BESIDE_PARAMETER);
  // A `split` wins over `beside`: it is the newer layout.
  const existingSide = sidePathOf(address.search);
  if (beside === null) {
    return query.has(SPLIT_PARAMETER) ? { kind: "pair", main: mainPathOf(address), side: existingSide } : null;
  }
  query.delete(BESIDE_PARAMETER);
  query.delete(SPLIT_PARAMETER);
  // The route table names the Thread, and decodes its ID (a malformed one stays as it is).
  const threadRoute = matchPath(ROUTE_PATTERNS.thread, address.pathname) ?? matchPath(ROUTE_PATTERNS.threadHostOffer, address.pathname);
  const onThread = threadRoute !== null;
  const threadId = query.get(BESIDE_THREAD_PARAMETER);

  if (beside === "ontology") {
    const side = ontologyRoute(query);
    for (const key of Object.values(ONTOLOGY_QUERY_KEYS)) query.delete(key);
    return { kind: "pair", main: pathWith(address, query), side: existingSide ?? side };
  }
  if (beside === "thread" && !onThread) {
    query.delete(BESIDE_THREAD_PARAMETER);
    return { kind: "pair", main: pathWith(address, query), side: existingSide ?? (threadId === null ? null : appPaths.thread(threadId)) };
  }
  const main = pathWith(address, query);
  const routeThreadId = threadRoute?.params["threadId"];
  if (beside === "source" && routeThreadId !== undefined && existingSide === null) {
    return { kind: "subjectOfThread", main, threadId: routeThreadId };
  }
  return { kind: "pair", main, side: existingSide };
}

/** The route of a Thread's subject, a document `VersionRef` or a `SegmentRef`, or `null` without one. */
export function subjectRoute(subject: string | null): AppPath | null {
  const address = subject === null ? null : parseContentAddress(subject);
  if (address?.document.version == null) {
    return null;
  }
  return appPaths.sourceDocument({ documentId: address.document.documentId, version: address.document.version }, address.segmentPath);
}
